import React from 'react';
import { MAIL_TYPE_LABELS, OrganizerInsights, OrganizerPortalEvent } from '@radevents/shared';
import { INK, MUTED, BERRY } from '../organizerSpace';
import MetricHint from './MetricHint';
import { favoriteTeaser } from './favoriteTeaser';
import { PremiumCta } from './chrome';

/**
 * Was die Spalten bedeuten — einmal geschrieben, von Kachel und Tabellenkopf gelesen.
 *
 * Zwei Fassungen desselben Satzes laufen auseinander, sobald eine gepflegt wird — und es sind
 * genau die Sätze, an denen ein Verein misst, ob er der Zahl glauben kann.
 */
const HINTS: Record<string, string> = {
  Aufrufe: 'Wie oft die Event-Seite geöffnet wurde.',
  Besucher: 'Wie viele Menschen das waren, jeder einmal pro Tag gezählt.',
  'Auf deinen Link geklickt': 'Klicks auf deinen Anmelde-, Ausschreibungs- oder Website-Link.',
  'Zum Kalender hinzugefügt': 'Als Termin in den eigenen Kalender übernommen.',
  Vormerkungen: 'So viele Personen haben das Event vorgemerkt.',
  Benachrichtigungen: 'E-Mails, die wir zu deinen Events verschickt haben.',
};

const hint = (label: string) => <MetricHint label={label} text={HINTS[label]} />;

interface Props {
  organizerId: number;
  /** Die eigenen Events — für den Vorgeschmack, solange Premium fehlt. */
  events?: OrganizerPortalEvent[];
  insights: OrganizerInsights | null;
  /** Warum es keine Zahlen gibt — meistens: kein Premium. */
  insightsError: string | null;
  onPremiumHint: () => void;
  /** Es liegt schon eine Premium-Anfrage bei uns — dann ist die Sperrkarte kein Kaufweg mehr. */
  premiumRequested?: boolean;
}

/**
 * Reiter *Statistiken*: die bezahlte Auskunft, oder der Haken an ihrer Stelle.
 *
 * Ausgelagert aus `OrganizerManage` — der Inhalt ist unverändert.
 */
const InsightsTab: React.FC<Props> = ({
  organizerId, events = [], insights, insightsError, onPremiumHint, premiumRequested,
}) => {
  const teaser = favoriteTeaser(events);
  return (
  <section>
    {/* Nicht „Fehler beim Laden", sondern die Erklärung an der Stelle, an der die Zahlen
        stünden — plus eine Vorschau, die zeigt, dass hier wirklich etwas wartet. Das
        Gerüst darunter trägt bewusst KEINE erfundenen Werte: eine unscharfe Tabelle mit
        ausgedachten Zahlen wäre eine Behauptung über diese Events. */}
    {insightsError && (
      <>
        <div className="vb-lockcard">
          <div className="vb-lockcard-main">
            <div style={{
              fontFamily: "'Space Mono',monospace", fontSize: 10.5, letterSpacing: '.1em',
              textTransform: 'uppercase', color: BERRY,
            }}>
              Statistiken gehören zu Premium
            </div>
            <h3 className="vb-title" style={{ marginTop: 6, color: INK }}>
              Verstehe, wie deine Events genutzt werden.
            </h3>
            <p>
              Mit Premium siehst du für jedes Event Aufrufe, Besucher, Vormerkungen,
              Kalendereinträge und Klicks auf deinen Anmeldelink.
            </p>
          </div>
          {/* Kein Preis hier: er steht im Dialog, den der Knopf öffnet, und auf der Verkaufskarte
              im Reiter *Sichtbarkeit & Promotion*. An einer Sperrkarte, die erklärt, was fehlt, ist er
              eine dritte Stelle für dieselbe Zahl. */}
          <PremiumCta requested={premiumRequested} onClick={onPremiumHint}>
            Statistiken freischalten
          </PremiumCta>
        </div>
        {/* Ein Vorgeschmack, keine Vorschau der Auswertung: **eine** Zahl, das staerkste Event.
            Alle Statistiken gehoeren zu Premium, auch die Favoriten (01.09.2026) — vorher stand
            hier eine ganze Tabelle mit Favoritenzahlen, also ein Stueck des Produkts umsonst.
            Unter vier Vormerkungen sagt der Vorgeschmack nichts (siehe favoriteTeaser). */}
        {teaser && (
          <p className="vb-teaser">
            <b>{teaser.favorites}</b>
            <span>
              Leute haben „{teaser.title}" vorgemerkt. Mit Premium siehst du das für jedes Event,
              dazu Aufrufe, Besucher, Klicks auf deinen Link und Kalender-Einträge.
            </span>
          </p>
        )}
      </>
    )}

    {insights && (
      <>
        {/* Kacheln statt der frueheren Textzeile (01.09.2026): dieselben vier Summen, aber
            als Werte lesbar statt als Satz. Die Auskunft bleibt die Tabelle darunter — welches
            Event wie lief —, deshalb stehen die Kacheln in einer Reihe und nicht als Wand.
            Die Reihenfolge folgt dem Weg eines Besuchers: sehen, sein, weiterklicken, merken. */}
        <div className="vb-stats">
          {/* Das Fragezeichen sitzt an der Beschriftung, nicht an der Zahl: gefragt wird nach dem
              Begriff. Es ist ein echter Knopf — mit der Tastatur erreichbar und auf dem Handy
              antippbar, anders als ein `title`-Attribut, das beides nicht ist. */}
          {([
            ['Aufrufe', insights.totalViews],
            ['Besucher', insights.totalUniqueVisitors],
            ['Auf deinen Link geklickt', insights.totalClicks],
            ['Vormerkungen', insights.totalFavorites],
            ['Benachrichtigungen', insights.totalNotified],
          ] as [string, number][]).map(([label, value]) => (
            <div className={`vb-stat${label === 'Auf deinen Link geklickt' ? ' vb-stat--out' : ''}`} key={label}>
              <b>{value}</b>
              <span>{label} {hint(label)}</span>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 13, color: MUTED, margin: '0 0 22px' }}>
          {new Date(insights.from).toLocaleDateString('de-DE')} bis{' '}
          {new Date(insights.to).toLocaleDateString('de-DE')}.
        </p>

        {/* Was die Benachrichtigungen gebracht haben — die einzige Zahl, die der
            Veranstalter selbst nicht schätzen kann. „Wir erinnern deine Teilnehmer" ist ein
            Verkaufsversprechen; ohne Zahl bleibt es eine Behauptung. */}
        {insights.notifications.length > 0 && (
          <div style={{ margin: '0 0 26px' }}>
            <p className="vb-figures">Davon nach Anlass</p>
            <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {insights.notifications.map(n => (
                <li key={n.mailType} style={{ fontSize: 13.5, color: INK, background: '#F5F3F3', borderRadius: 9, padding: '8px 11px' }}>
                  <strong>{n.recipients}×</strong> {MAIL_TYPE_LABELS[n.mailType] ?? n.mailType}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table className="vb-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Aufrufe {hint('Aufrufe')}</th>
                <th>Besucher {hint('Besucher')}</th>
                <th className="vb-col-out">Auf deinen Link geklickt {hint('Auf deinen Link geklickt')}</th>
                <th>Zum Kalender hinzugefügt {hint('Zum Kalender hinzugefügt')}</th>
                <th>Vormerkungen {hint('Vormerkungen')}</th>
              </tr>
            </thead>
            <tbody>
              {insights.rows.map(row => {
                const max = Math.max(1, ...insights.rows.map(r => r.views));
                return (
                  <tr key={row.eventId}>
                    <td>
                      {/* Neues Tab: der Veranstalter steht hier mit gewähltem Reiter und Zeitraum
                          in seinem Arbeitsbereich — ihn wegzunavigieren hieße, dass er nach jedem
                          Blick auf ein Event von vorn anfängt. */}
                      <a className="vb-event-link" href={`/events/${row.eventId}`}
                        target="_blank" rel="noopener noreferrer"
                        title="Event-Seite in neuem Tab öffnen">
                        {row.title}<span className="lh-ext" aria-hidden>↗</span>
                      </a>
                      <span style={{ color: MUTED }}>
                        {' '}· {new Date(`${row.eventDate}T00:00:00`).toLocaleDateString('de-DE')}
                      </span>
                    </td>
                    <td className="vb-bar" style={{ ['--w' as any]: `${Math.round((row.views / max) * 100)}%` }}>
                      <span>{row.views}</span>
                    </td>
                    <td>{row.uniqueVisitors}</td>
                    <td className="vb-col-out">{row.clicks}</td>
                    <td>{row.icalDownloads}</td>
                    <td>{row.favorites}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </>
    )}
  </section>
  );
};

export default InsightsTab;
